import { DOMParser } from "@xmldom/xmldom";
import { XMLParser } from "fast-xml-parser";
import type {
  DeckConfiguration,
  DeckPosition,
} from "@/state/run/useDeckLayoutState";
import type { VisibleWhenCondition } from "@/automation/catalog/catalogTypes";

export type { VisibleWhenCondition };

export interface VolumeBasedTipOption {
  tipTypeId: string;
  volumeMin?: number;
  volumeMax?: number;
  unit?: string;
}

function getVolumeRangesFromLabwareType(labwareType: LabwareType): {
  min?: number;
  max?: number;
} {
  const specs = labwareType.specifications;
  const result: { min?: number; max?: number } = {};

  for (const spec of specs) {
    if (spec.name === "minVolume") {
      result.min = Number(spec.value);
    }
    if (spec.name === "maxVolume") {
      result.max = Number(spec.value);
    }
  }

  return result;
}

export interface ConfigParam {
  name: string;
  id: string;
  type: string;
  required: boolean;
  hidden?: boolean;
  min?: number;
  max?: number;
  unit?: string;
  options?: string[];
  volumeOptions?: VolumeBasedTipOption[];
  role?: string;
  replicates?: string;
  default?: string;
  /** Pins this labware param to a fixed deck position id (e.g. "A13"). */
  deckPositionOverride?: string;
  // Conditional visibility — see VisibleWhenCondition in catalog/catalogTypes.
  visibleWhen?: VisibleWhenCondition;
  // csv_map specific metadata (present only when type === "csv_map")
  csvMap?: CsvMapSpec;
}

export interface ConfigStep {
  name: string;
  id: string;
  params: ConfigParam[];
  compatiblePipettingPatterns?: string[];
  compatibleHeads?: string[]; // Head IDs this step is compatible with (empty = no restriction)
  description?: string;
  aiDescription?: string; // AI-facing description (from the `ai_description` attr)
  expandedParameters?: string;
  displayPipettingPattern?: boolean;
  options?: string[];
  role?: string;
  replicates?: string;
  requireLiquidClass?: boolean;
  sourceMultiSelect?: boolean; // If true, allow multi-select on source plate instead of destination
}

export interface ParsedConfig {
  steps: ConfigStep[];
}

export interface CsvMapSpec {
  map: string; // e.g. "transfer" | "normalisation" | "dilution"
  volume?: {
    min?: number;
    max?: number;
  };
  sampleVolume?: {
    min?: number;
    max?: number;
  };
  bufferVolume?: {
    min?: number;
    max?: number;
  };
}

export interface LabwareSpec {
  name: string;
  value: string | number;
  unit?: string;
}

export interface LabwareType {
  id: string;
  label: string;
  specifications: LabwareSpec[];
  // Catalog metadata — carried from Firestore catalog through XML
  mdbId?: number;
  category?: string; // "plate" | "tip" | "adapter" | "device"
  rows?: number;
  tipStand?: string; // tips only, e.g. "stand105", "tipbox"
  adapterType?: string; // adapters only, e.g. "8channel", "12channel"
  adapterStand?: string; // adapters only, e.g. "stand120"
  // Normalized deployment type for tips (derived from tipStand)
  deploymentType?: "tiptray" | "tiprack" | "tipbox";
  // Device role for device-category labware ("return" = empty tip-return rack)
  deviceRole?: "waste" | "trash" | "return";
  // For deviceRole "return": nominal tip volumes (µL) this rack accepts.
  supportedTipVolumes?: number[];
  // Classification tags from catalog (e.g. "plate", "DW", "tall", "tiptray")
  tags?: string[];
  // If true, only one instance of this labware should be used per protocol (e.g., shared tip box)
  onePerProtocol?: boolean;
  // If true, track column usage and allocate additional tipboxes when capacity is exceeded (Biomek only)
  trackTipCapacity?: boolean;
}

export interface HeadSpec {
  name: string;
  value: string; // Always a string for head specs
  unit?: string;
}

export interface Head {
  id: string;
  label: string;
  specifications: HeadSpec[];
}

export interface Robot {
  id: string;
  label: string;
  deckLayoutId: string;
  compatibleHeads: string[];
}

export interface LiquidClassSpec {
  name: string;
  value: string | number | boolean;
  unit?: string;
  min?: number;
  max?: number;
}

export interface LiquidClass {
  id: string;
  specifications: LiquidClassSpec[];
  default?: boolean;
}

export interface ParsedFullConfig {
  labware: LabwareType[];
  heads: Head[];
  robots: Robot[];
  steps: ConfigStep[];
  deckLayouts: DeckConfiguration[];
  liquidClasses: LiquidClass[];
}

/**
 * Parses XML configuration string into a structured object
 * @param xmlString The XML configuration string to parse
 * @returns Parsed configuration object
 */
export function parseConfigXml(xmlString: string): ParsedConfig {
  try {
    const parser = new DOMParser();
    const xmlDoc = parser.parseFromString(xmlString, "text/xml");

    const result: ParsedConfig = { steps: [] };

    // Get all step elements
    const stepElements = xmlDoc.getElementsByTagName("step");

    for (let i = 0; i < stepElements.length; i++) {
      const stepElement = stepElements[i];
      const stepName = stepElement.getAttribute("name") || `Step ${i + 1}`;
      const stepId = stepElement.getAttribute("id") || stepName;
      const stepDescription =
        stepElement.getAttribute("description") || undefined;
      const stepAiDescription =
        stepElement.getAttribute("ai_description") || undefined;
      const expandedParameters =
        stepElement.getAttribute("expanded_parameters") || undefined;
      const pipettingPatternAttr =
        stepElement.getAttribute("pipetting_pattern");
      const pipettingPattern =
        pipettingPatternAttr === "true"
          ? true
          : pipettingPatternAttr === "false"
            ? false
            : undefined;

      const step: ConfigStep = {
        name: stepName,
        id: stepId,
        params: [],
        description: stepDescription,
        aiDescription: stepAiDescription,
        expandedParameters: expandedParameters,
        displayPipettingPattern: pipettingPattern,
      };

      // Parse compatible-pipetting-patterns (comma separated string)
      const compatiblePipettingPatternsAttr = stepElement.getAttribute(
        "compatible-pipetting-patterns"
      );
      if (compatiblePipettingPatternsAttr) {
        step.compatiblePipettingPatterns = compatiblePipettingPatternsAttr
          .split(",")
          .map((s) => s.trim());
      }

      // Parse compatible-heads (comma separated string)
      const compatibleHeadsAttr =
        stepElement.getAttribute("compatible-heads");
      if (compatibleHeadsAttr) {
        step.compatibleHeads = compatibleHeadsAttr
          .split(",")
          .map((s) => s.trim());
      }

      // Parse source-multi-select
      const sourceMultiSelectAttr =
        stepElement.getAttribute("source-multi-select");
      if (sourceMultiSelectAttr === "true") {
        step.sourceMultiSelect = true;
      }

      // Get all param elements within this step
      const paramElements = stepElement.getElementsByTagName("param");

      for (let j = 0; j < paramElements.length; j++) {
        const paramElement = paramElements[j];
        const paramName =
          paramElement.getAttribute("name") || `Parameter ${j + 1}`;
        const paramId = paramElement.getAttribute("id") || paramName;
        const paramType = paramElement.getAttribute("type") || "unknown";
        const paramRequired = paramElement.getAttribute("required") === "true";
        const paramHidden = paramElement.getAttribute("hidden") === "true";

        const param: ConfigParam = {
          name: paramName,
          id: paramId,
          type: paramType,
          required: paramRequired,
          hidden: paramHidden,
        };

        // Parse min, max, and unit attributes if they exist
        if (paramElement.hasAttribute("min")) {
          param.min = parseFloat(paramElement.getAttribute("min") || "0");
        }

        if (paramElement.hasAttribute("max")) {
          param.max = parseFloat(paramElement.getAttribute("max") || "0");
        }

        if (paramElement.hasAttribute("unit")) {
          param.unit = paramElement.getAttribute("unit") || "";
        }

        // csv_map specific attributes
        if (paramType === "csv_map") {
          const csvMap: CsvMapSpec = {
            map: paramElement.getAttribute("map") || "",
          };

          // Generic volume bounds (transfer/dilution)
          if (
            paramElement.hasAttribute("volume_min") ||
            paramElement.hasAttribute("volume_max")
          ) {
            csvMap.volume = {};
            if (paramElement.hasAttribute("volume_min")) {
              const v = paramElement.getAttribute("volume_min");
              if (v != null) csvMap.volume.min = Number(v);
            }
            if (paramElement.hasAttribute("volume_max")) {
              const v = paramElement.getAttribute("volume_max");
              if (v != null) csvMap.volume.max = Number(v);
            }
          }

          // Normalisation-specific bounds
          if (
            paramElement.hasAttribute("sample_volume_min") ||
            paramElement.hasAttribute("sample_volume_max")
          ) {
            csvMap.sampleVolume = {};
            if (paramElement.hasAttribute("sample_volume_min")) {
              const v = paramElement.getAttribute("sample_volume_min");
              if (v != null) csvMap.sampleVolume.min = Number(v);
            }
            if (paramElement.hasAttribute("sample_volume_max")) {
              const v = paramElement.getAttribute("sample_volume_max");
              if (v != null) csvMap.sampleVolume.max = Number(v);
            }
          }

          if (
            paramElement.hasAttribute("buffer_volume_min") ||
            paramElement.hasAttribute("buffer_volume_max")
          ) {
            csvMap.bufferVolume = {};
            if (paramElement.hasAttribute("buffer_volume_min")) {
              const v = paramElement.getAttribute("buffer_volume_min");
              if (v != null) csvMap.bufferVolume.min = Number(v);
            }
            if (paramElement.hasAttribute("buffer_volume_max")) {
              const v = paramElement.getAttribute("buffer_volume_max");
              if (v != null) csvMap.bufferVolume.max = Number(v);
            }
          }

          param.csvMap = csvMap;
        }

        // Parse role attribute if it exists
        if (paramElement.hasAttribute("role")) {
          param.role = paramElement.getAttribute("role") || undefined;
        }

        // Parse replicates attribute if it exists
        if (paramElement.hasAttribute("replicates")) {
          const replicatesAttr = paramElement.getAttribute("replicates");
          param.replicates = replicatesAttr ? replicatesAttr : undefined;
        }

        // Parse conditional visibility (emitted as a JSON-encoded attribute).
        if (paramElement.hasAttribute("visible-when")) {
          const visibleWhenAttr = paramElement.getAttribute("visible-when");
          if (visibleWhenAttr) {
            try {
              param.visibleWhen = JSON.parse(
                visibleWhenAttr
              ) as VisibleWhenCondition;
            } catch (e) {
              // Malformed JSON here indicates a config-generation bug. Surface it
              // (don't silently swallow) and leave the param unconditional.
              console.error(
                `[systemConfigParser] Failed to parse visible-when for param "${paramId}":`,
                visibleWhenAttr,
                e
              );
            }
          }
        }

        // Parse options for select type parameters
        const optionsElement = paramElement.getElementsByTagName("options")[0];
        if (optionsElement) {
          const optionElements = optionsElement.getElementsByTagName("option");
          param.options = [];

          for (let k = 0; k < optionElements.length; k++) {
            const optionElement = optionElements[k];
            const optionText = optionElement.textContent || "";
            if (optionText) {
              param.options.push(optionText);
            }
          }
        }

        // Check if any options have volume attributes
        const volumeOptionsElement =
          paramElement.getElementsByTagName("options")[0];
        if (volumeOptionsElement) {
          const volumeOptionElements =
            volumeOptionsElement.getElementsByTagName("option");
          let hasVolumeOptions = false;

          // First pass: check if any options have volume attributes
          for (let k = 0; k < volumeOptionElements.length; k++) {
            const optionElement = volumeOptionElements[k];
            if (
              optionElement.hasAttribute("volume-min") ||
              optionElement.hasAttribute("volume-max")
            ) {
              hasVolumeOptions = true;
              break;
            }
          }

          // If we found volume options, parse them
          if (hasVolumeOptions) {
            param.volumeOptions = [];

            for (let k = 0; k < volumeOptionElements.length; k++) {
              const optionElement = volumeOptionElements[k];
              const tipTypeId = optionElement.textContent || "";

              if (tipTypeId) {
                // Find the labware type in the full config
                const fullConfig = parseFullConfigXml(xmlString);
                const labwareType = fullConfig.labware.find(
                  (lw) => lw.id === tipTypeId
                );

                if (labwareType) {
                  const volumeRanges =
                    getVolumeRangesFromLabwareType(labwareType);
                  const volumeOption: VolumeBasedTipOption = {
                    tipTypeId,
                    volumeMin: volumeRanges.min,
                    volumeMax: volumeRanges.max,
                  };

                  param.volumeOptions.push(volumeOption);
                }
              }
            }
          }
        }

        step.params.push(param);
      }

      result.steps.push(step);
    }

    return result;
  } catch (error) {
    console.error("Error parsing XML:", error);
    throw new Error("Failed to parse configuration XML");
  }
}

export function parseFullConfigXml(xmlString: string): ParsedFullConfig {
  const parser = new XMLParser({
    ignoreAttributes: false,
    attributeNamePrefix: "",
    parseAttributeValue: true,
    isArray: (name, jpath) => {
      // Always treat these as arrays even if they have single items
      const arrayPaths = [
        "validation-rules-schema.steps.step",
        "validation-rules-schema.steps.step.param",
        "validation-rules-schema.heads.head",
        "validation-rules-schema.labware-types.type",
        "validation-rules-schema.robots.robot",
        "validation-rules-schema.deck-layouts.deck-layout",
        "validation-rules-schema.liquid-classes.type",
        "validation-rules-schema.liquid-classes.type.specifications.spec",
      ];
      return arrayPaths.includes(jpath);
    },
  });
  const xmlDoc = parser.parse(xmlString);
  const labware: LabwareType[] = [];
  const heads: Head[] = [];
  const robots: Robot[] = [];
  const deckLayouts: DeckConfiguration[] = [];
  const liquidClasses: LiquidClass[] = [];
  let steps: ConfigStep[] = [];

  try {
    // Parse heads if they exist
    if (
      xmlDoc["validation-rules-schema"] &&
      xmlDoc["validation-rules-schema"]["heads"]
    ) {
      const headElements = xmlDoc["validation-rules-schema"]["heads"].head;

      if (headElements) {
        const headArray = Array.isArray(headElements)
          ? headElements
          : [headElements];

        headArray.forEach((head: any) => {
          try {
            if (head && head.id && head.label && head.specifications) {
              const specs: HeadSpec[] = [];

              if (Array.isArray(head.specifications.spec)) {
                head.specifications.spec.forEach((spec: any) => {
                  if (spec && spec.name) {
                    specs.push({
                      name: spec.name,
                      value:
                        spec.name === "dimensions"
                          ? String(spec.value)
                          : isNaN(Number(spec.value))
                            ? spec.value
                            : Number(spec.value),
                      unit: spec.unit,
                    });
                  }
                });
              } else if (
                head.specifications.spec &&
                head.specifications.spec.name
              ) {
                specs.push({
                  name: head.specifications.spec.name,
                  value:
                    head.specifications.spec.name === "dimensions"
                      ? String(head.specifications.spec.value)
                      : isNaN(Number(head.specifications.spec.value))
                        ? head.specifications.spec.value
                        : Number(head.specifications.spec.value),
                  unit: head.specifications.spec.unit,
                });
              }

              heads.push({
                id: head.id,
                label: head.label,
                specifications: specs,
              });
            }
          } catch (error) {
            console.error("Error parsing head:", error);
          }
        });
      }
    }

    // Parse labware types if they exist
    if (
      xmlDoc["validation-rules-schema"] &&
      xmlDoc["validation-rules-schema"]["labware-types"]
    ) {
      const labwareElements =
        xmlDoc["validation-rules-schema"]["labware-types"].type;

      if (labwareElements) {
        const labwareArray = Array.isArray(labwareElements)
          ? labwareElements
          : [labwareElements];

        labwareArray.forEach((lw: any) => {
          try {
            // A labware type is valid with just id + label; specifications are
            // optional (a tip-return rack carries no wells/specs, only its
            // tip-stand token + device-role + supported-tips). Previously a
            // missing <specifications> dropped the whole type from the config.
            if (lw && lw.id && lw.label) {
              const specs: LabwareSpec[] = [];

              if (lw.specifications && Array.isArray(lw.specifications.spec)) {
                lw.specifications.spec.forEach((spec: any) => {
                  if (spec && spec.name) {
                    specs.push({
                      name: spec.name,
                      value: isNaN(Number(spec.value))
                        ? spec.value
                        : Number(spec.value),
                      unit: spec.unit,
                    });
                  }
                });
              } else if (
                lw.specifications &&
                lw.specifications.spec &&
                lw.specifications.spec.name
              ) {
                specs.push({
                  name: lw.specifications.spec.name,
                  value: isNaN(Number(lw.specifications.spec.value))
                    ? lw.specifications.spec.value
                    : Number(lw.specifications.spec.value),
                  unit: lw.specifications.spec.unit,
                });
              }

              const parsedMdbId = lw["mdb-id"];
              const parsedRows = lw["rows"];

              labware.push({
                id: lw.id,
                label: lw.label,
                specifications: specs,
                mdbId: parsedMdbId != null ? Number(parsedMdbId) : undefined,
                category: lw["category"] || undefined,
                rows: parsedRows != null ? Number(parsedRows) : undefined,
                tipStand: lw["tip-stand"] || undefined,
                adapterType: lw["adapter-type"] || undefined,
                adapterStand: lw["adapter-stand"] || undefined,
                deploymentType: lw["deployment-type"] || undefined,
                deviceRole: lw["device-role"] || undefined,
                supportedTipVolumes: lw["supported-tips"]
                  ? String(lw["supported-tips"])
                      .split(",")
                      .map((v) => Number(v.trim()))
                      .filter((v) => !Number.isNaN(v))
                  : undefined,
                tags: lw["tags"] ? String(lw["tags"]).split(",").filter(Boolean) : undefined,
                onePerProtocol:
                  lw["one-per-protocol"] === true ||
                  lw["one-per-protocol"] === "true",
                trackTipCapacity:
                  lw["track-tip-capacity"] === true ||
                  lw["track-tip-capacity"] === "true",
              });
            }
          } catch (e) {
            console.error("Error parsing labware item:", e);
          }
        });
      }
    }

    // Parse robots if they exist
    if (
      xmlDoc["validation-rules-schema"] &&
      xmlDoc["validation-rules-schema"]["robots"]
    ) {
      const robotElements = xmlDoc["validation-rules-schema"]["robots"].robot;

      if (robotElements) {
        const robotArray = Array.isArray(robotElements)
          ? robotElements
          : [robotElements];

        robotArray.forEach((robot: any) => {
          try {
            if (robot && robot.id && robot.label) {
              const deckLayoutId = robot["deck-layout"]
                ? robot["deck-layout"].id
                : null;
              let compatibleHeads: string[] = [];

              if (
                robot.compatibility &&
                robot.compatibility["head-types"] &&
                robot.compatibility["head-types"]["head-type"]
              ) {
                const headTypes =
                  robot.compatibility["head-types"]["head-type"];

                if (Array.isArray(headTypes)) {
                  compatibleHeads = headTypes;
                } else {
                  compatibleHeads = [headTypes];
                }
              }

              robots.push({
                id: robot.id,
                label: robot.label,
                deckLayoutId: deckLayoutId || "standard-dual-deck", // Default value if not found
                compatibleHeads: compatibleHeads,
              });
            }
          } catch (error) {
            console.error("Error parsing robot:", error);
          }
        });
      }
    }

    // Parse steps using the fast-xml-parser result
    if (
      xmlDoc["validation-rules-schema"] &&
      xmlDoc["validation-rules-schema"].steps
    ) {
      const stepElements = xmlDoc["validation-rules-schema"].steps.step;

      if (stepElements) {
        const stepArray = Array.isArray(stepElements)
          ? stepElements
          : [stepElements];

        steps = stepArray.map((step: any) => {
          const params: ConfigParam[] = [];
          let hasLiquidClassParam = false;

          if (step.param) {
            const paramArray = Array.isArray(step.param)
              ? step.param
              : [step.param];

            paramArray.forEach((param: any) => {
              const options = param.options?.option;

              // Check if this is a liquid class parameter
              if (param.type === "select" && param.id === "liquid_class") {
                hasLiquidClassParam = true;
              }

              // Parse regular options and check for volume attributes
              let parsedOptions: string[] | undefined;
              let parsedVolumeOptions: VolumeBasedTipOption[] | undefined;

              if (options) {
                const optionArray = Array.isArray(options)
                  ? options
                  : [options];
                parsedOptions = [];
                parsedVolumeOptions = [];

                optionArray.forEach((opt: any) => {
                  const tipTypeId =
                    typeof opt === "string"
                      ? opt
                      : opt["#text"] || opt.toString();

                  // Add to options array
                  if (tipTypeId && !parsedOptions!.includes(tipTypeId)) {
                    parsedOptions!.push(tipTypeId);
                  }

                  // Find the labware type for this tip
                  const labwareType = labware.find((lw) => lw.id === tipTypeId);
                  if (labwareType) {
                    const volumeRanges =
                      getVolumeRangesFromLabwareType(labwareType);
                    const volumeOption: VolumeBasedTipOption = {
                      tipTypeId,
                      volumeMin: volumeRanges.min,
                      volumeMax: volumeRanges.max,
                    };

                    parsedVolumeOptions!.push(volumeOption);
                  }
                });

                // If no volume options were found, clear the array
                if (parsedVolumeOptions!.length === 0) {
                  parsedVolumeOptions = undefined;
                }
              }
              // csv_map specific attributes (fast-xml-parser)
              let csvMap: CsvMapSpec | undefined;
              if (param.type === "csv_map") {
                csvMap = {
                  map:
                    typeof param.map === "string"
                      ? param.map
                      : String(param.map ?? ""),
                };

                // Generic volume bounds (transfer/dilution)
                if (
                  param.volume_min !== undefined ||
                  param.volume_max !== undefined
                ) {
                  csvMap.volume = {};
                  if (param.volume_min !== undefined) {
                    csvMap.volume.min = Number(param.volume_min);
                  }
                  if (param.volume_max !== undefined) {
                    csvMap.volume.max = Number(param.volume_max);
                  }
                }

                // Normalisation-specific bounds
                if (
                  param.sample_volume_min !== undefined ||
                  param.sample_volume_max !== undefined
                ) {
                  csvMap.sampleVolume = {};
                  if (param.sample_volume_min !== undefined) {
                    csvMap.sampleVolume.min = Number(param.sample_volume_min);
                  }
                  if (param.sample_volume_max !== undefined) {
                    csvMap.sampleVolume.max = Number(param.sample_volume_max);
                  }
                }

                if (
                  param.buffer_volume_min !== undefined ||
                  param.buffer_volume_max !== undefined
                ) {
                  csvMap.bufferVolume = {};
                  if (param.buffer_volume_min !== undefined) {
                    csvMap.bufferVolume.min = Number(param.buffer_volume_min);
                  }
                  if (param.buffer_volume_max !== undefined) {
                    csvMap.bufferVolume.max = Number(param.buffer_volume_max);
                  }
                }
              }

              // Merge regular options with volume options for backward compatibility
              const configParam: ConfigParam = {
                name: param.name,
                id: param.id,
                type: param.type,
                required: param.required || false,
                hidden: param.hidden || false,
                min: param.min,
                max: param.max,
                unit: param.unit,
                volumeOptions: parsedVolumeOptions,
                default: param.default,
                role: param.role,
                options: parsedOptions,
                replicates: param.replicates,
                csvMap: csvMap,
                deckPositionOverride: param["deck-position-override"] || undefined,
              };

              // Conditional visibility (JSON-encoded attribute).
              const visibleWhenRaw = param["visible-when"];
              if (typeof visibleWhenRaw === "string" && visibleWhenRaw !== "") {
                try {
                  configParam.visibleWhen = JSON.parse(
                    visibleWhenRaw
                  ) as VisibleWhenCondition;
                } catch (e) {
                  // Malformed JSON indicates a config-generation bug. Surface it
                  // (don't silently swallow) and leave the param unconditional.
                  console.error(
                    `[systemConfigParser] Failed to parse visible-when for param "${param.id}":`,
                    visibleWhenRaw,
                    e
                  );
                }
              }

              params.push(configParam);
            });
          }

          // Extract compatible pipetting patterns (kebab-case in XML -> camelCase in object)
          const compatiblePatternsString =
            step["compatible-pipetting-patterns"];
          const compatiblePipettingPatterns =
            typeof compatiblePatternsString === "string"
              ? compatiblePatternsString.split(",").map((p) => p.trim())
              : undefined;

          // Convert kebab/underscore case XML attributes to camelCase booleans/strings
          const pipettingPatternValue = step.pipetting_pattern;
          const expandedParametersValue = step.expanded_parameters;
          // Parse source-multi-select attribute for enabling multi-select on source plates
          const sourceMultiSelectValue = step["source-multi-select"] === true || step["source-multi-select"] === "true";

          // Parse compatible-heads (comma separated string of head IDs)
          const compatibleHeadsString = step["compatible-heads"];
          const compatibleHeads =
            typeof compatibleHeadsString === "string"
              ? compatibleHeadsString.split(",").map((h) => h.trim())
              : undefined;

          return {
            name: step.name,
            id: step.id,
            params: params,
            compatiblePipettingPatterns: compatiblePipettingPatterns,
            compatibleHeads: compatibleHeads,
            description: step.description,
            aiDescription: step.ai_description,
            expandedParameters: expandedParametersValue,
            displayPipettingPattern: pipettingPatternValue,
            requireLiquidClass: hasLiquidClassParam,
            sourceMultiSelect: sourceMultiSelectValue || undefined,
          };
        });
      }
    }

    // Parse deck layouts if they exist
    if (
      xmlDoc["validation-rules-schema"] &&
      xmlDoc["validation-rules-schema"]["deck-layouts"]
    ) {
      const layoutElements =
        xmlDoc["validation-rules-schema"]["deck-layouts"]["deck-layout"];

      if (layoutElements) {
        const layoutsArray = Array.isArray(layoutElements)
          ? layoutElements
          : [layoutElements];

        layoutsArray.forEach((layout: any) => {
          try {
            if (layout && layout.id && layout.label) {
              const positions: DeckPosition[] = [];
              const sections: {
                id: string;
                label: string;
                positionIds: string[];
                gridCols?: number;
                deckArea?: string;
                orientation?: string;
              }[] = [];

              // Parse positions
              if (layout.positions && layout.positions.position) {
                const posArray = Array.isArray(layout.positions.position)
                  ? layout.positions.position
                  : [layout.positions.position];

                posArray.forEach((pos: any) => {
                  if (pos && pos.id) {
                    positions.push({
                      id: pos.id,
                      allowedLabware: pos.allowedLabware
                        ? pos.allowedLabware.split(",")
                        : undefined,
                      fixedLabware: pos["fixed-labware"] || undefined,
                      coversPositions: pos["covers-positions"]
                        ? String(pos["covers-positions"]).split(",")
                        : undefined,
                    });
                  }
                });
              }

              // Parse sections
              if (layout.sections && layout.sections.section) {
                const sectArray = Array.isArray(layout.sections.section)
                  ? layout.sections.section
                  : [layout.sections.section];

                sectArray.forEach((sect: any) => {
                  if (sect && sect.id && sect.label && sect["position-refs"]) {
                    const posRefs = Array.isArray(
                      sect["position-refs"]["position-ref"]
                    )
                      ? sect["position-refs"]["position-ref"]
                      : [sect["position-refs"]["position-ref"]];

                    const positionIds: string[] = [];
                    posRefs.forEach((ref: any) => {
                      if (ref && ref.id) {
                        positionIds.push(ref.id);
                      }
                    });

                    sections.push({
                      id: sect.id,
                      label: sect.label,
                      positionIds,
                      gridCols: sect["grid-cols"] ? Number(sect["grid-cols"]) : undefined,
                      deckArea: sect["deck-area"] || undefined,
                      orientation: sect["orientation"] || undefined,
                    });
                  }
                });
              }

              deckLayouts.push({
                id: layout.id,
                label: layout.label,
                positions,
                sections,
              });
            }
          } catch (error) {
            console.error("Error parsing deck layout:", error);
          }
        });
      }
    }

    // Validate fixed labware references
    const labwareTypeIds = new Set(labware.map((lw) => lw.id));
    deckLayouts.forEach((layout) => {
      layout.positions.forEach((pos) => {
        if (pos.fixedLabware && !labwareTypeIds.has(pos.fixedLabware)) {
          console.error(
            `Invalid config: Position ${pos.id} in layout ${layout.id} ` +
            `references non-existent labware type: ${pos.fixedLabware}`
          );
          // Remove invalid reference to prevent runtime errors
          pos.fixedLabware = undefined;
        }
      });
    });

    // Parse liquid classes if they exist
    if (
      xmlDoc["validation-rules-schema"] &&
      xmlDoc["validation-rules-schema"]["liquid-classes"]
    ) {
      const liquidClassElements =
        xmlDoc["validation-rules-schema"]["liquid-classes"].type;

      if (liquidClassElements) {
        const liquidClassArray = Array.isArray(liquidClassElements)
          ? liquidClassElements
          : [liquidClassElements];

        liquidClassArray.forEach((liquidClass: any) => {
          try {
            if (liquidClass && liquidClass.id && liquidClass.specifications) {
              const specs: LiquidClassSpec[] = [];

              if (Array.isArray(liquidClass.specifications.spec)) {
                liquidClass.specifications.spec.forEach((spec: any) => {
                  if (spec && spec.name) {
                    // Handle boolean values
                    let value: string | number | boolean = spec.value;
                    if (typeof spec.value === "string") {
                      if (spec.value.toLowerCase() === "true") {
                        value = true;
                      } else if (spec.value.toLowerCase() === "false") {
                        value = false;
                      } else if (!isNaN(Number(spec.value))) {
                        value = Number(spec.value);
                      }
                    }

                    specs.push({
                      name: spec.name,
                      value: value,
                      unit: spec.unit,
                      min: spec.min ? Number(spec.min) : undefined,
                      max: spec.max ? Number(spec.max) : undefined,
                    });
                  }
                });
              } else if (
                liquidClass.specifications.spec &&
                liquidClass.specifications.spec.name
              ) {
                let value: string | number | boolean =
                  liquidClass.specifications.spec.value;
                if (typeof liquidClass.specifications.spec.value === "string") {
                  if (
                    liquidClass.specifications.spec.value.toLowerCase() ===
                    "true"
                  ) {
                    value = true;
                  } else if (
                    liquidClass.specifications.spec.value.toLowerCase() ===
                    "false"
                  ) {
                    value = false;
                  } else if (
                    !isNaN(Number(liquidClass.specifications.spec.value))
                  ) {
                    value = Number(liquidClass.specifications.spec.value);
                  }
                }

                specs.push({
                  name: liquidClass.specifications.spec.name,
                  value: value,
                  unit: liquidClass.specifications.spec.unit,
                  min: liquidClass.specifications.spec.min
                    ? Number(liquidClass.specifications.spec.min)
                    : undefined,
                  max: liquidClass.specifications.spec.max
                    ? Number(liquidClass.specifications.spec.max)
                    : undefined,
                });
              }

              liquidClasses.push({
                id: liquidClass.id,
                specifications: specs,
                default:
                  liquidClass.default === "true" ||
                  liquidClass.default === true,
              });
            }
          } catch (error) {
            console.error("Error parsing liquid class:", error);
          }
        });
      }
    }

    // Return the parsed config
    return {
      labware,
      heads,
      robots,
      steps,
      deckLayouts,
      liquidClasses,
    };
  } catch (error) {
    console.error("Error parsing XML:", error);
    throw new Error("Failed to parse configuration XML");
  }
}
